import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Snackbar, Alert, CircularProgress, Typography, Tabs, Tab } from '@mui/material';
import { DynamicLenderSubmissionMappingModalTabs, VendorOpportunity } from 'modules/vendorOpportunity';
import { LenderDataTab } from './lenderDataTab';
import { DocumentsTab } from './documentsTab';
import { VendorProfile, PortalConfiguration, LenderType, LenderFieldMap } from '@trnsact/trnsact-shared-types';
import { LenderProfile } from 'types';
import { lenderTypeMap } from 'pages/VendorOpportunity/LenderSubmissionMappingModals/constants';
import { useFetchDynamicLenderSubmissionData } from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/hooks/useFetchDynamicLenderSubmissionData';
import { useDlmSubmission } from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/hooks/useDlmSubmission';
import { FormProvider, useForm } from 'react-hook-form';
import { LenderMappingsFormValues } from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/types';
import { filterOutPersonalGuarantorFields } from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/lib';

export interface Props {
  vo: VendorOpportunity;
  vp: VendorProfile;
  lender: LenderProfile;
  setDynamicLenderMappingCallback: (lenderProfileId: string, mappings: LenderFieldMap[]) => void;
  handleClose: () => void;
  submitCreditApp: (sharedDocuments: any) => void;
  portalConfiguration: PortalConfiguration;
  transactionSummary: string;
}

export const DynamicLenderSubmissionMappingModalBody = ({
  vo,
  vp,
  lender,
  setDynamicLenderMappingCallback,
  handleClose,
  submitCreditApp,
  portalConfiguration,
  transactionSummary,
}: Props) => {
  const [sharedDocuments, setSharedDocuments] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<DynamicLenderSubmissionMappingModalTabs>(
    DynamicLenderSubmissionMappingModalTabs.LenderData
  );
  const [corpOnly, setCorpOnly] = useState(false);

  const { dynamicLenderMappings, structuredMappings, isLoading, setDynamicLenderMappings } =
    useFetchDynamicLenderSubmissionData({
      lenderType: (lender.submissionMethod || lenderTypeMap.get(lender.lenderChannelCode)) as LenderType,
      vendorOpportunityId: vo.vendorOpportunityId,
      lenderProfileDynamicsId: lender.lenderProfileId,
      transactionSummary,
    });

  const submissionReadyMappings = useMemo(
    () => (corpOnly ? filterOutPersonalGuarantorFields(dynamicLenderMappings) : dynamicLenderMappings),
    [corpOnly, dynamicLenderMappings]
  );

  // Keeps the lender's submitted payload in sync with both field edits and the corpOnly toggle.
  useEffect(() => {
    if (!submissionReadyMappings.length) return;
    setDynamicLenderMappingCallback(lender.lenderProfileId, submissionReadyMappings);
  }, [submissionReadyMappings, lender.lenderProfileId, setDynamicLenderMappingCallback]);

  const { openDlmErrorSnackbar, dlmErrorSnackbarMessage, handleDlmFormSubmit, setOpenDlmErrorSnackbar } =
    useDlmSubmission({
      dynamicLenderMappings: submissionReadyMappings,
      submitCreditApp,
      handleClose,
      sharedDocuments,
    });

  const form = useForm<LenderMappingsFormValues>({ mode: 'onBlur' });

  const handleMappingsChange = useCallback(
    (updatedMappings: LenderFieldMap[]) => {
      setDynamicLenderMappings(updatedMappings);
    },
    [setDynamicLenderMappings]
  );

  if (isLoading || !dynamicLenderMappings?.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading Submission Data...
        </Typography>
      </Box>
    );
  }

  return (
    <FormProvider {...form}>
      <>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Lender Data" value={DynamicLenderSubmissionMappingModalTabs.LenderData} />
          <Tab label="Documents" value={DynamicLenderSubmissionMappingModalTabs.Documents} />
        </Tabs>

        <Box display={activeTab === DynamicLenderSubmissionMappingModalTabs.LenderData ? 'block' : 'none'}>
          <LenderDataTab
            lender={lender}
            portalConfiguration={portalConfiguration}
            dynamicLenderMappings={dynamicLenderMappings}
            structuredMappings={structuredMappings}
            onMappingsChange={handleMappingsChange}
            onCorpOnlyChange={setCorpOnly}
          />
        </Box>

        <Box display={activeTab === DynamicLenderSubmissionMappingModalTabs.Documents ? 'block' : 'none'}>
          <DocumentsTab vo={vo} vp={vp} lender={lender} onSharedDocumentsChange={setSharedDocuments} />
        </Box>

        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          open={openDlmErrorSnackbar}
          autoHideDuration={6000}
          onClose={() => setOpenDlmErrorSnackbar(false)}
        >
          <Alert severity="error">{dlmErrorSnackbarMessage}</Alert>
        </Snackbar>
        <Box display="flex" justifyContent="center" my={2}>
          <Button variant="contained" color="primary" onClick={form.handleSubmit(handleDlmFormSubmit)}>
            Submit
          </Button>
        </Box>
      </>
    </FormProvider>
  );
};
